import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Button from '@/components/ui/button'
import Container from '@/components/ui/container'
import Icon from '@/components/ui/icon'
import Link from '@/components/ui/link'
import LinkCta from '@/components/ui/link-cta'
import SectionHeader from '@/components/ui/section-header'

const serviceIcons = ['insights', 'build', 'monitoring']
const serviceNumbers = ['01', '02', '03']

export default function Services() {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()

  const items = t('services.items', { returnObjects: true }) as {
    title: string
    description: string
  }[]

  return (
    <section
      ref={ref}
      id="services"
      className="py-16 md:py-24 lg:py-32 bg-surface-container-lowest"
    >
      <Container>
        <div
          className={`flex flex-col md:flex-row justify-between items-end mb-12 md:mb-16 lg:mb-24 gap-8 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader
            label={t('services.label')}
            heading={t('services.heading')}
            className="max-w-2xl"
          />
          <p className="text-on-surface-variant max-w-sm mb-2">{t('services.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((service, i) => (
            <div
              key={i}
              className={`group relative bg-surface-container-high p-5 md:p-8 transition-all duration-500 hover:-translate-y-2 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: visible ? `${300 + i * 100}ms` : '0ms' }}
            >
              <div className="mb-12 flex justify-between">
                <Icon name={serviceIcons[i]} filled className="text-primary" />
                <span className="text-white font-display text-sm font-bold opacity-20 group-hover:opacity-100 transition-opacity">
                  {serviceNumbers[i]}
                </span>
              </div>
              <h4 className="font-display text-xl font-bold mb-4">{service.title}</h4>
              <p className="text-on-surface-variant text-sm leading-relaxed mb-8">
                {service.description}
              </p>
              <LinkCta>{t('services.explore')}</LinkCta>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 md:mt-16 lg:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: visible ? '700ms' : '0ms' }}
        >
          <p className="text-on-surface-variant max-w-2xl text-base md:text-lg">
            {t('services.cta.description')}
          </p>
          <Link href="/contact">
            <Button variant="gradient">{t('services.cta.button')}</Button>
          </Link>
        </div>
      </Container>
    </section>
  )
}
